import type { Appointment, ConflictingAppointment } from '../wire';

/**
 * One intent, as it sits on the device (spec §8.2: "an ordered list of intents,
 * each with a client-generated ULID and an idempotency key").
 *
 * ## The two ids, and why there are two
 *
 * `id` is the ULID this device minted. It is sent as `client_intent_id` in the
 * body, and `core-api` echoes it on the response — which is how an optimistic
 * row finds out which server row it became (`intents.ts`, `withIntentId`).
 *
 * `idempotencyKey` is what the server keys the replay memo on. It is
 * **`intent:${id}` deliberately**, because that is the exact key `readIntent`
 * derives when a request carries only `client_intent_id`. Sending both with that
 * relationship means the header and the body can never disagree — `readIntent`
 * says the header wins and that "a device that sets them to different values has
 * a bug" — and it means a retry that loses the header still hits the same memo.
 *
 * It is minted **once, at enqueue**, and stored. Every attempt reuses it. That
 * single fact is what makes replay safe: attempt three of an intent whose
 * attempt one committed gets the original response back, byte for byte, rather
 * than a second booking.
 *
 * ## `describe` is a snapshot, not a lookup
 *
 * The task list has to say *"Reschedule Dana?"* while the iPad is still in
 * airplane mode, and possibly for a client whose row was never mirrored. So the
 * human-facing facts are copied onto the intent when it is created. Reading them
 * back later out of a mirror that may have moved on would be a small merge, and
 * §8.3 does not do merges.
 */

export type IntentKind =
  | 'appointment.create'
  | 'appointment.reschedule'
  /**
   * S1 — *"this one ran long."* A per-booking length change, and a **separate
   * kind from `reschedule`** for the reason `core-api`'s registry gives: a move
   * keeps the span it was booked at on every door, and a `duration_min` folded
   * into that intent would have made the one rule with three enforcers into a
   * rule with an exception.
   */
  | 'appointment.duration'
  | 'appointment.cancel'
  | 'appointment.status'
  | 'client.create'
  | 'client.update'
  | 'client.archive'
  | 'client.identity.add'
  | 'client.identity.remove'
  | 'preference.create'
  | 'preference.update'
  | 'preference.delete'
  /**
   * A1 — how long a service takes this one guest, remembered and forgotten.
   * The target names the service and — for one length of it (0048) — the
   * option: `intents.ts` picks the door by whether `target.option_id` is there.
   */
  | 'duration.set'
  | 'duration.clear'
  | 'service.create'
  | 'service.update'
  /**
   * S2 — the team. A person or a room, created and edited by the owner: the
   * name, the week, the order, and which login she is.
   *
   * Queued like every other settings edit rather than sent directly, for §8.2's
   * reason and one of its own: the Team screen reads the **mirror**, so an owner
   * in a tunnel who adds Thursday to somebody's week sees it immediately and the
   * write lands when the radio does. The one thing on that screen that is *not*
   * queued is the seat roster it links against, which is an online read — see
   * `TeamSeat` in `wire.ts`.
   */
  | 'resource.create'
  | 'resource.update'
  | 'hours.set'
  | 'blackout.create'
  | 'blackout.delete'
  /* MVP 4 — the money (spec §6.5, §7.3, §7.4). */
  | 'settlement.create'
  | 'certificate.issue'
  | 'credit.add'
  | 'product.create'
  | 'product.update'
  /**
   * C1 → B4 — the shelf, moved by the till.
   *
   * The Reservations screen moves one *online* (it is a race with other people
   * and the buttons come from the server, which is `useRemote`'s whole
   * argument). This kind exists for the one move that is **not** a screen of its
   * own: the moment a settlement is rung up against a pickup order, the jar has
   * left the shelf, and that fact has to survive the same basement the
   * settlement does. It is queued *after* the settlement deliberately — the
   * outbox is ordered, and "collected" without a sale beside it is the wrong
   * half to land first.
   */
  | 'reservation.status'
  /**
   * V5 — the desk sells what the site sells. A stay booked at the front desk
   * and a seat sold on the dock, through the same store seams the guest site
   * uses (`bookStay`, `bookDeparture`).
   *
   * Queued rather than sent directly, for `reservation.status`'s reason and
   * one of their own: a sale is the one thing a tunnel must not lose, and the
   * screens that make them are online (`useRemote`) — so there is no mirror
   * row for a settled answer to update, `settlementKind` is `none`, and the
   * screen re-reads the day or the manifest when the drain lands it.
   */
  | 'stay.book'
  | 'departure.book';

export type IntentStatus = 'queued' | 'inflight' | 'done' | 'rejected';

export interface IntentDescription {
  /** "Book Dana — Swedish Massage", "Edit Dana Whitfield". Shown as-is. */
  what: string;
  /** The guest, when there is one. The subject of the task sentence. */
  person?: string | undefined;
  /** The instant the intent is about, for "This 2 PM…". */
  whenIso?: string | undefined;
}

export interface RejectionRecord {
  code: string;
  message: string;
  status: number;
  detail?: Record<string, unknown> | undefined;
  conflicts?: ConflictingAppointment[] | undefined;
  at: string;
}

export interface OutboxEntry {
  /** The client ULID. Sent as `client_intent_id`. */
  id: string;
  /** Total order. Replay follows it exactly (spec §8.2: "in order"). */
  seq: number;
  kind: IntentKind;
  /** Path parameters the request needs — appointment id, client id, and so on. */
  target: Record<string, string>;
  payload: Record<string, unknown>;
  /** `intent:${id}`. Stable across every attempt, forever. */
  idempotencyKey: string;
  createdAt: string;
  status: IntentStatus;
  attempts: number;
  /** Epoch ms; the drain will not touch this entry before it. */
  nextAttemptAt: number;
  describe: IntentDescription;
  /**
   * The row to show immediately, before the server has answered. Only creates
   * have one — an edit shows through the mirrored row it edits.
   */
  optimistic?: Appointment | undefined;
  lastError?: string | undefined;
  rejection?: RejectionRecord | undefined;
  settledAt?: string | undefined;
}

/** Queued or in flight: work the device still owes the cloud. */
export function isPending(entry: OutboxEntry): boolean {
  return entry.status === 'queued' || entry.status === 'inflight';
}
